import { defineStore } from 'pinia'
import { ref, computed, onUnmounted, watch } from 'vue'
import type { DeviceKind, AudioPlayer } from '@/customTypes'
// Stores
import { useSoundEffectsStore } from '@/stores/SoundEffectsStore'
import { useDataStore } from '@/stores/DataStore'
import { useActiveScreenerStore } from '@/stores/activeScreenerStore'

import Logger from '@/services/Logger.js'

const logger = new Logger('AudioAnalyserStore', true)

interface NamedAudioProcessor {
  name: string
  processor: (audioData: Float32Array) => void
}

export const useAudioAnalyserStore = defineStore('audioAnalyser', () => {
  const soundEffectsStore = useSoundEffectsStore()
  const dataStore = useDataStore()

  const SMOOTHING_TIME: number = 0.1
  const FFT_RANGE: number = 1024

  const DEFAULT_AUDIO_URL = computed(
    () =>
      `${_ASSETS_BUCKET_URL_}/${dataStore.screenerTemplateId}/tracks-and-sound-effects/TEST_AUDIO.mp3?v=${_ASSETS_VERSION_}`
  )

  // Audio Analyser properties
  const audioContextRef = ref<AudioContext | null>(null)
  const processorRef = ref<ScriptProcessorNode | null>(null)
  const audioPlayer = ref<AudioPlayer | undefined>(undefined)
  const oldVolumeMap = ref<Map<string, number>>(new Map())
  interface VolumeCallbackEntry {
    callbacks: Set<Function>
    generation: number
  }

  const volumeChangeCallbacks = ref<Map<string, VolumeCallbackEntry>>(new Map())
  const callbackGenerations = ref<Map<string, number>>(new Map())
  const showRecorderOutput = ref<boolean>(true)
  const deviceSetupMap = ref<Record<string, DeviceKind[]>>({})
  const contextState = ref<AudioContextState | null>(null)
  const outputPlaybackActive = ref<boolean>(false)

  // Recording Properties
  const recordingStreamRef = ref<MediaStream | null>(null)
  const recordingStartTime = ref<number>(0)
  const recorder = ref<MediaRecorder | null>(null)
  const inputDuration = ref<number>(0)
  const inputBlob = ref<Blob | null>(null)
  const isRecording = ref<boolean>(false)
  const chunks = ref<Blob[]>([])
  const recordingFinishedCallback = ref<Function | null>(null)

  // Custom audio processors
  const customAudioProcessors = ref<NamedAudioProcessor[]>([])

  const analyser = ref<AnalyserNode | null>(null)
  const TEST_AUDIO_TRACK_ID = 'audio-output-test-track'

  watch(
    audioContextRef,
    (newContext, oldContext) => {
      if (oldContext) {
        oldContext.removeEventListener('statechange', handleStateChange)
      }

      if (newContext) {
        contextState.value = newContext.state
        newContext.addEventListener('statechange', handleStateChange)
      }
    },
    { immediate: true }
  )

  async function initiateTrackToAnalyse(audioUrl?: string) {
    const assignedTrack = await soundEffectsStore.createTestOutputTrack(
      TEST_AUDIO_TRACK_ID,
      audioUrl || DEFAULT_AUDIO_URL.value
    )
    audioPlayer.value = assignedTrack
    return assignedTrack
  }

  function getAnalyserKey(deviceKind: DeviceKind, recordingStream?: MediaStream | string | null) {
    if (deviceKind === 'audiooutput') return 'audio-output'
    if (!recordingStream && recordingStreamRef.value) {
      return recordingStreamRef.value.id
    }
    if (!recordingStream) return 'audioinput'

    return typeof recordingStream === 'string' ? recordingStream : recordingStream.id
  }

  function registerVolumeCallback(callbackKey: string, callback?: Function) {
    if (!callback) return
    logger.info('Registering volume callback request', { callbackKey })

    const existingEntry = volumeChangeCallbacks.value.get(callbackKey)
    if (existingEntry) {
      logger.info('Replacing existing volume callbacks for key', callbackKey)
    }
    removeVolumeCallbacks(callbackKey)
    const nextGeneration = (callbackGenerations.value.get(callbackKey) ?? 0) + 1
    callbackGenerations.value.set(callbackKey, nextGeneration)

    const callbacksForKey = new Set<Function>([callback])
    volumeChangeCallbacks.value.set(callbackKey, {
      callbacks: callbacksForKey,
      generation: nextGeneration
    })

    logger.info('Registered volume callback', { callbackKey, generation: nextGeneration })
  }

  function removeVolumeCallbacks(callbackKey: string, callback?: Function) {
    const entry = volumeChangeCallbacks.value.get(callbackKey)
    if (!entry) {
      oldVolumeMap.value.delete(callbackKey)
      return
    }

    if (callback) {
      entry.callbacks.delete(callback)
    } else {
      entry.callbacks.clear()
    }

    if (entry.callbacks.size === 0) {
      logger.info('Clearing volume callback entry for key', callbackKey)
      volumeChangeCallbacks.value.delete(callbackKey)
      callbackGenerations.value.delete(callbackKey)
    }

    oldVolumeMap.value.delete(callbackKey)
  }

  function teardownProcessor() {
    if (!processorRef.value) return

    processorRef.value.disconnect()
    processorRef.value.onaudioprocess = null
    processorRef.value = null
  }

  async function rebuildOutputAnalyser(
    audioContext: AudioContext,
    callback?: Function
  ): Promise<void> {
    if (!audioPlayer.value) {
      logger.warn('No audio player available when attempting to rebuild audio output analyser')
      return
    }

    if (analyser.value) {
      analyser.value.disconnect()
      analyser.value = null
    }

    teardownProcessor()

    analyser.value = audioContext.createAnalyser()
    analyser.value.fftSize = FFT_RANGE
    analyser.value.smoothingTimeConstant = SMOOTHING_TIME

    await audioPlayer.value.connectAnalyzer(analyser.value)
    const callbackKey = getAnalyserKey('audiooutput')
    setupScriptProcessor(audioContext, analyser.value, undefined, callbackKey)
    if (callback) {
      registerVolumeCallback(callbackKey, callback)
    }
    showRecorderOutput.value = true
  }

  async function setupAudioAnalyser(
    audioContext: AudioContext,
    deviceKind: DeviceKind,
    recordingStream: MediaStream | null,
    callback?: Function
  ) {
    if (!recordingStream && deviceKind !== 'audiooutput') {
      return
    }

    const streamId = recordingStream?.id || 'audio-output'

    if (deviceKind === 'audiooutput' && recordingStream && deviceSetupMap.value['audio-output']) {
      delete deviceSetupMap.value['audio-output']
    }

    if (!deviceSetupMap.value[streamId]) {
      deviceSetupMap.value[streamId] = []
    }

    const alreadySetup = deviceSetupMap.value[streamId].includes(deviceKind)

    if (alreadySetup) {
      logger.info(
        `Audio analyser already set up for ${deviceKind} on recordingStream with id ${streamId}. Skipping setup.`
      )

      if (deviceKind === 'audiooutput') {
        logger.info('Rebuilding audio output analyser after device change', { streamId })
        await rebuildOutputAnalyser(audioContext, callback)
        return
      }

      // Reset callback in case of component unmount.
      if (callback) {
        const callbackKey = getAnalyserKey(deviceKind, recordingStream)
        registerVolumeCallback(callbackKey, callback)
      }
      return
    }
    logger.info(
      `Analyser not setup attempting to setup for ${deviceKind} on recordingStream with id ${streamId}`
    )
    audioContextRef.value = audioContext
    if (recordingStream) {
      recordingStreamRef.value = recordingStream
    }
    const callbackKey = getAnalyserKey(deviceKind, recordingStream)
    if (callback) {
      registerVolumeCallback(callbackKey, callback)
    }
    showRecorderOutput.value = true

    analyser.value = audioContext.createAnalyser()
    analyser.value.fftSize = FFT_RANGE
    analyser.value.smoothingTimeConstant = SMOOTHING_TIME
    let mediaSource

    if (deviceKind === 'audiooutput') {
      if (audioPlayer.value) {
        await audioPlayer.value.connectAnalyzer(analyser.value)
      } else {
        logger.warn('No audio player available when setting up audio output analyser')
      }
    } else {
      mediaSource = audioContext.createMediaStreamSource(recordingStream as MediaStream)
      if (!mediaSource) {
        logger.error('No media source found')
        return
      }
      mediaSource.connect(analyser.value)
    }

    setupScriptProcessor(audioContext, analyser.value, mediaSource, callbackKey)
    deviceSetupMap.value[streamId].push(deviceKind)
  }

  function setupScriptProcessor(
    activeAudioContext: AudioContext,
    analyser: AnalyserNode,
    mediaSource: AudioBufferSourceNode | MediaStreamAudioSourceNode | undefined,
    callbackKey: string
  ) {
    const processor = activeAudioContext.createScriptProcessor(2048, 1, 1)

    processor.onaudioprocess = (event) => {
      const inputData = event.inputBuffer.getChannelData(0)
      updateIndicator(analyser, callbackKey)
      customAudioProcessors.value.forEach(({ processor }) => processor(inputData))
    }
    processorRef.value = processor
    mediaSource?.connect(analyser)
    analyser.connect(processor)
    processor.connect(activeAudioContext.destination)
  }

  function updateIndicator(audioData: AnalyserNode, callbackKey: string) {
    if (dataStore.screenerInProgress && !isRecording.value) return

    let calculatedVolume = 0
    let average = 0

    const analyser = audioData
    const freqArray = new Uint8Array(analyser.frequencyBinCount)
    analyser.getByteFrequencyData(freqArray)
    const freqTotal = freqArray.reduce((sum, value) => sum + value, 0)
    average = freqTotal / freqArray.length
    calculatedVolume = (average / 100) * 2

    if (!showRecorderOutput.value) return

    const previousVolume = oldVolumeMap.value.get(callbackKey)
    if (previousVolume === calculatedVolume) return

    oldVolumeMap.value.set(callbackKey, calculatedVolume)

    const entry = volumeChangeCallbacks.value.get(callbackKey)
    if (!entry || entry.callbacks.size === 0) return

    const expectedGeneration = callbackGenerations.value.get(callbackKey)
    if (expectedGeneration !== entry.generation) {
      logger.info('Skipping stale volume callback generation', {
        callbackKey,
        expectedGeneration,
        entryGeneration: entry.generation
      })
      entry.callbacks.clear()
      volumeChangeCallbacks.value.delete(callbackKey)
      oldVolumeMap.value.delete(callbackKey)
      return
    }

    entry.callbacks.forEach((callback) => {
      callback({ volume: calculatedVolume, average })
    })
  }

  function destroyAudioAnalyser(recordingStream?: MediaStream, deviceKind?: DeviceKind) {
    logger.info('Destroying audio analyser', processorRef.value)

    if (audioContextRef.value) {
      audioContextRef.value.removeEventListener('statechange', handleStateChange)
    }

    destroyAudioPlayer()
    destroyRecorder()

    if (processorRef.value) {
      processorRef.value.disconnect()

      processorRef.value.onaudioprocess = null

      processorRef.value = null
    }

    if (!deviceKind) {
      resetAudioSetup()
      return
    }

    const callbackKey = getAnalyserKey(deviceKind, recordingStream || recordingStreamRef.value)
    removeVolumeCallbacks(callbackKey)

    if (!recordingStream) {
      if (deviceKind === 'audiooutput') {
        cleanUpDeviceSetupMap('audio-output', deviceKind)
        return
      }
      resetAudioSetup()
      return
    }
    cleanUpDeviceSetupMap(recordingStream, deviceKind)
  }

  function handleStateChange(event: Event) {
    const ctx = event.target as AudioContext
    contextState.value = ctx.state

    // Automatically resume if suspended
    if (ctx.state === 'suspended') {
      ctx.resume()
    } else if (ctx.state !== 'running') {
      stopSound()
    }
  }

  function cleanUpDeviceSetupMap(recordingStream: MediaStream | string, deviceKind: DeviceKind) {
    const streamId = typeof recordingStream === 'string' ? recordingStream : recordingStream.id
    if (deviceSetupMap.value[streamId]) {
      const deviceKinds = deviceSetupMap.value[streamId]

      const deviceKindIndex = deviceKinds.indexOf(deviceKind)
      if (deviceKindIndex !== -1) {
        deviceKinds.splice(deviceKindIndex, 1)

        logger.info(
          `Audio analyser torn down successfully for deviceKind ${deviceKind} on stream ${streamId}`
        )
      } else {
        logger.info(`deviceKind ${deviceKind} not found for stream ${streamId}. Skipping removal.`)
      }

      if (deviceKinds.length === 0) {
        delete deviceSetupMap.value[streamId]
        logger.info(`No more device kinds left for stream ${streamId}. Entry deleted.`)
      }
    }
    if (deviceKind === 'audiooutput' && deviceSetupMap.value['audio-output']) {
      delete deviceSetupMap.value['audio-output']
      logger.info('Removed fallback audio-output analyser entry')
    }
  }

  function destroyRecorder() {
    if (recorder.value) {
      recorder.value.stop()
      recorder.value = null
    }
    resetRecordingValues()
  }

  function destroyAudioPlayer() {
    logger.info('Destroying audio player', audioPlayer.value)

    if (!audioPlayer.value) return

    soundEffectsStore.removeTrackSync(TEST_AUDIO_TRACK_ID)

    audioPlayer.value = undefined
    outputPlaybackActive.value = false
  }

  function startRecordingSound(): Promise<void> {
    if (!recordingStreamRef.value) {
      logger.info('No recording stream reference found')
      return Promise.resolve()
    }

    isRecording.value = true
    showRecorderOutput.value = true
    return new Promise((resolve, reject) => {
      try {
        logger.info('Starting recording sound')
        recordingStartTime.value = Date.now()
        recorder.value = new MediaRecorder(recordingStreamRef.value as MediaStream)

        recorder.value.ondataavailable = (event) => {
          chunks.value.push(event.data)
        }

        recorder.value.onstop = onRecordingFinished.bind(null, resolve)

        recorder.value.onerror = (error) => {
          throw new Error(`Error: ${error}`)
        }

        recorder.value.start()
      } catch (error) {
        dataStore.setScreenerError(
          'recordingError',
          'Sorry, the screener failed to start recording the users answer. Please refresh the page and try again. \n If the problem persists, please contact the support team.'
        )
        reject(`${error}`)
      }
    })
  }

  function stopRecordingSound(onFinishedCallback?: Function) {
    logger.info('Stopping recording sound')
    showRecorderOutput.value = false
    recordingFinishedCallback.value = onFinishedCallback || null

    if (recorder.value) {
      recorder.value.stop()
    } else if (dataStore.screenerInProgress) {
      logger.error('No recorder found when trying to stop recording')
    }
  }

  function onRecordingFinished(resolve: any) {
    logger.info('Recording finished')
    inputBlob.value = new Blob(chunks.value, { type: 'audio/wav' })
    const recordingEndTime = Date.now()
    inputDuration.value = (recordingEndTime - recordingStartTime.value) / 1000
    isRecording.value = false

    if (recordingFinishedCallback.value) {
      recordingFinishedCallback.value({
        inputBlob: inputBlob.value,
        inputDuration: inputDuration.value
      })
    }
    destroyRecorder()
    resolve(true)
  }

  function playSound() {
    if (audioPlayer.value) {
      audioPlayer.value.start()
    }
  }

  function stopSound() {
    if (audioPlayer.value) {
      audioPlayer.value.end()
    }
    outputPlaybackActive.value = false
  }

  function resetRecordingValues() {
    logger.info('Resetting recording values')
    chunks.value = []
    recordingStartTime.value = 0
    inputDuration.value = 0
    recorder.value = null
    inputBlob.value = null
  }

  function handleTrackPlaybackChange(trackId: string, isPlaying: boolean) {
    if (trackId !== TEST_AUDIO_TRACK_ID) return
    logger.info('Handling test track playback change', { trackId, isPlaying })
    outputPlaybackActive.value = isPlaying
  }

  function addCustomAudioProcessor(name: string, processor: (audioData: Float32Array) => void) {
    const existingIndex = customAudioProcessors.value.findIndex((p) => p.name === name)
    if (existingIndex !== -1) {
      logger.info(`Audio processor with name "${name}" already exists. Replacing.`)
      customAudioProcessors.value[existingIndex] = { name, processor }
    } else {
      logger.info(`Adding audio processor: ${name}`)
      customAudioProcessors.value.push({ name, processor })
    }
  }

  function removeCustomAudioProcessor(name: string) {
    const index = customAudioProcessors.value.findIndex((p) => p.name === name)
    if (index !== -1) {
      customAudioProcessors.value.splice(index, 1)
      logger.info(`Removed audio processor: ${name}`)
    } else {
      logger.warn(`No audio processor found with name: ${name}`)
    }
  }

  function resetAudioSetup() {
    if (!deviceSetupMap.value) return

    // Disconnect and clean up all audio nodes
    if (analyser.value) {
      analyser.value.disconnect()
      analyser.value = null
    }

    // Reset all refs
    audioContextRef.value = null
    recordingStreamRef.value = null
    volumeChangeCallbacks.value.clear()
    callbackGenerations.value.clear()
    oldVolumeMap.value.clear()
    showRecorderOutput.value = true

    deviceSetupMap.value = {}
    logger.info('Audio analyser torn down successfully')
  }

  onUnmounted(() => {
    resetAudioSetup()
  })
  return {
    isRecording: computed(() => isRecording.value),
    sampleRate: computed(() => audioContextRef.value?.sampleRate),
    setupAudioAnalyser,
    destroyAudioAnalyser,
    startRecordingSound,
    stopRecordingSound,
    getIsRecording: computed(() => isRecording.value),
    getRecordingStartTime: computed(() => recordingStartTime.value),
    setShowRecorderOutput: (show: boolean) => {
      showRecorderOutput.value = show
    },
    hasAnalyserSetUp: computed(
      () => !!(processorRef.value && analyser.value && audioContextRef.value?.sampleRate)
    ),
    hasAudioPlayerSetUp: computed(() => !!audioPlayer.value),
    playSound,
    stopSound,
    initiateTrackToAnalyse,
    outputPlaybackActive: computed(() => outputPlaybackActive.value),
    handleTrackPlaybackChange,
    addCustomAudioProcessor,
    removeCustomAudioProcessor
  }
})
